import { Fragment, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap-v5';
import {
    IAnalisarConfigurarResultado,
    IAnaliseContasResultado,
    IAssistenteComando,
    IConfigurarRegrasResultado,
    IDiagnosticoLancamento,
    IExplicarLancamentosResultado,
    IPlanoConta
} from '../../interfaces/IAssistenteIa';

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const pct = (parte: number, total: number) => (total > 0 ? `${Math.round((parte * 1000) / total) / 10}%` : '—');

const CORES_QUALIDADE: Record<string, string> = { BOA: '#50CD89', REGULAR: '#FFC700', RUIM: '#F1416C' };

const MOTIVOS: Record<string, string> = {
    SEM_REGRA_CONFIGURADA: 'Nenhuma regra configurada na conta',
    SEM_CHAVE_EXTRAIDA: 'As regras não extraem chave deste histórico',
    CHAVE_SEM_CONTRAPARTIDA: 'Chave sem contrapartida do outro lado',
    CONTRAPARTIDA_JA_CONCILIADA: 'Contrapartida já conciliada com outro lançamento',
    VALOR_NAO_FECHA: 'Contrapartida pela chave, mas o valor não fecha',
    PAR_DISPONIVEL: 'Há contrapartida pendente que fecha',
    SO_VALOR_COINCIDE: 'Só o valor coincide (sem chave em comum)',
    HISTORICO_GENERICO: 'Histórico genérico'
};

function lerResultado<T>(comando: IAssistenteComando): T | null {
    if (!comando.resultadoJson) return null;
    try {
        return JSON.parse(comando.resultadoJson) as T;
    } catch {
        return null;
    }
}

const Analise = ({ resultado }: { resultado: IAnaliseContasResultado }) => {
    const [aberta, setAberta] = useState<number | null>(null);

    return (
        <>
            {resultado.consolidacao && (
                <div className="mb-4 p-3" style={{ backgroundColor: '#fff', borderRadius: 8, whiteSpace: 'pre-wrap', fontSize: 13 }}>
                    <strong>Parecer geral</strong>
                    <div className="mt-2">{resultado.consolidacao}</div>
                </div>
            )}
            <div className="table-responsive">
                <table className="table table-sm table-row-bordered align-middle" style={{ fontSize: 12 }}>
                    <thead>
                        <tr className="fw-bold text-muted">
                            <th>Conta</th>
                            <th>Natureza</th>
                            <th className="text-end">Lançamentos</th>
                            <th className="text-end">% conciliado</th>
                            <th>Histórico (IA)</th>
                            <th className="text-end">Qualificado</th>
                            <th className="text-end">Com nº documento</th>
                            <th className="text-end">Notas pareadas / fechando</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {resultado.contas.map(({ metricas: m, ia }) => (
                            <Fragment key={m.cartaoId}>
                                <tr>
                                    <td><strong>{m.conta}</strong><div className="text-muted">{m.descricao}</div></td>
                                    <td>{m.natureza === 'CLIENTE' ? 'Cliente' : 'Fornecedor'}</td>
                                    <td className="text-end">{m.totalLinhas.toLocaleString('pt-BR')}</td>
                                    <td className="text-end">{m.percentualConciliado}%</td>
                                    <td>
                                        {ia?.qualidadeHistorico && (
                                            <span className="badge" style={{ backgroundColor: CORES_QUALIDADE[ia.qualidadeHistorico] ?? '#7E8299' }}>{ia.qualidadeHistorico}</span>
                                        )}
                                    </td>
                                    <td className="text-end">{pct(m.historicoQualificado, m.linhasAmostradas)}</td>
                                    <td className="text-end">{pct(m.comNumeroDocumento, m.linhasAmostradas)}</td>
                                    <td className="text-end">{m.chavesComDoisLados.toLocaleString('pt-BR')} / {m.chavesFechandoValor.toLocaleString('pt-BR')}</td>
                                    <td>
                                        <button className="btn btn-sm btn-light" onClick={() => setAberta(aberta === m.cartaoId ? null : m.cartaoId)}>
                                            {aberta === m.cartaoId ? 'Fechar' : 'Detalhes'}
                                        </button>
                                    </td>
                                </tr>
                                {aberta === m.cartaoId && (
                                    <tr>
                                        <td colSpan={9} style={{ backgroundColor: '#fff' }}>
                                            <div className="row p-2">
                                                <div className="col-md-7">
                                                    <p className="mb-1"><strong>Padrão de conciliação:</strong> {ia?.padraoConciliacao}</p>
                                                    <p className="mb-1"><strong>Regra recomendada:</strong> {ia?.regraRecomendada}</p>
                                                    {ia && ia.riscos.length > 0 && (
                                                        <>
                                                            <strong>Riscos</strong>
                                                            <ul className="mb-1">{ia.riscos.map((r, i) => <li key={i}>{r}</li>)}</ul>
                                                        </>
                                                    )}
                                                    {ia?.observacoes && <p className="mb-1 text-muted">{ia.observacoes}</p>}
                                                    <p className="mb-0 text-muted">
                                                        Métricas de histórico sobre {m.linhasAmostradas.toLocaleString('pt-BR')} de {m.totalLinhas.toLocaleString('pt-BR')} lançamentos.
                                                        Regras já configuradas: {m.regrasConfiguradas.length > 0 ? m.regrasConfiguradas.join(', ') : 'nenhuma'}.
                                                    </p>
                                                </div>
                                                <div className="col-md-5">
                                                    <strong>Âncoras de documento</strong>
                                                    <div className="mb-2">{m.ancoras.map(a => `${a.texto} (${a.quantidade.toLocaleString('pt-BR')})`).join(' · ') || '—'}</div>
                                                    <strong>Históricos mais repetidos</strong>
                                                    <ul className="mb-0" style={{ fontFamily: 'monospace', fontSize: 11 }}>
                                                        {m.moldes.slice(0, 10).map((molde, i) => <li key={i}>{molde.texto} <span className="text-muted">({molde.quantidade.toLocaleString('pt-BR')})</span></li>)}
                                                    </ul>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

const Regras = ({ resultado }: { resultado: IConfigurarRegrasResultado }) => {
    const porIndice = useMemo(() => new Map((resultado.simulacao?.porRegra ?? []).map(p => [p.indice, p])), [resultado]);

    return (
        <>
            {resultado.simulacao && (
                <div className="mb-3 p-3" style={{ backgroundColor: '#fff', borderRadius: 8 }}>
                    <strong>Simulação na conta {resultado.conta}:</strong>{' '}
                    <span style={{ fontSize: 18 }}>{resultado.simulacao.percentualAntes}% → <strong>{resultado.simulacao.percentualDepois}%</strong></span>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                        {resultado.simulacao.pendentesSimulados.toLocaleString('pt-BR')} lançamentos pendentes simulados · {resultado.simulacao.totalLinhas.toLocaleString('pt-BR')} lançamentos na conta
                    </div>
                </div>
            )}
            <div className="table-responsive">
                <table className="table table-sm table-row-bordered align-middle" style={{ fontSize: 12 }}>
                    <thead>
                        <tr className="fw-bold text-muted">
                            <th>#</th>
                            <th>Origem</th>
                            <th>Classe / parâmetros</th>
                            <th className="text-end">Conciliados</th>
                            <th className="text-end">Com diferença</th>
                            <th className="text-end">Valor</th>
                            <th>Justificativa</th>
                        </tr>
                    </thead>
                    <tbody>
                        {resultado.regras.map((regra, i) => {
                            const stats = porIndice.get(i);
                            return (
                                <tr key={i} style={{ opacity: regra.descartada ? 0.5 : 1 }}>
                                    <td>{i + 1}</td>
                                    <td>{regra.existente ? 'Cadastrada' : `IA (rodada ${regra.rodada})`}</td>
                                    <td>
                                        <strong>{regra.classe}</strong>
                                        <div style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>{regra.parametros}</div>
                                    </td>
                                    <td className="text-end">{stats ? stats.conciliados.toLocaleString('pt-BR') : '—'}</td>
                                    <td className="text-end">{stats ? stats.divergentes.toLocaleString('pt-BR') : '—'}</td>
                                    <td className="text-end">{stats ? brl.format(stats.valorConciliado) : '—'}</td>
                                    <td>
                                        {regra.descartada ? <span className="text-danger">Descartada: {regra.motivoDescarte}</span> : regra.justificativa}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {resultado.recusadasPelaValidacao.length > 0 && (
                <div className="mb-2" style={{ fontSize: 12 }}>
                    <strong>Propostas recusadas pela validação:</strong>
                    <ul className="mb-0">{resultado.recusadasPelaValidacao.map((r, i) => <li key={i} style={{ wordBreak: 'break-all' }}>{r}</li>)}</ul>
                </div>
            )}
            {resultado.observacoes && <p style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}><strong>Observações da IA:</strong> {resultado.observacoes}</p>}
            {resultado.regrasGravadasIds.length > 0 && <p style={{ fontSize: 12 }}><strong>Regras gravadas:</strong> {resultado.regrasGravadasIds.join(', ')}</p>}
            <p className="text-muted mb-0" style={{ fontSize: 11 }}>{resultado.aviso}</p>
        </>
    );
};

const TabelaDiagnosticos = ({ diagnosticos }: { diagnosticos: IDiagnosticoLancamento[] }) => (
    <div className="table-responsive">
        <table className="table table-sm table-row-bordered align-middle" style={{ fontSize: 12 }}>
            <thead>
                <tr className="fw-bold text-muted">
                    <th>Lançamento</th>
                    <th>Histórico</th>
                    <th>Motivos</th>
                    <th>Contrapartidas candidatas</th>
                </tr>
            </thead>
            <tbody>
                {diagnosticos.map(d => (
                    <tr key={d.lancamentoId}>
                        <td>#{d.lancamentoId}<div className="text-muted">{d.tipo} · {d.data} · {brl.format(d.valor)}</div></td>
                        <td style={{ maxWidth: 260 }}>{d.historico}</td>
                        <td>{d.motivos.map(m => <div key={m}>{MOTIVOS[m] ?? m}</div>)}</td>
                        <td>
                            {d.candidatos.length === 0 ? '—' : d.candidatos.map(c => (
                                <div key={c.id}>#{c.id} {c.tipo} {brl.format(c.valor)} <span className="text-muted">({c.status}, por {c.origem === 'CHAVE' ? 'chave' : 'valor'})</span></div>
                            ))}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const Explicacao = ({ resultado }: { resultado: IExplicarLancamentosResultado }) => (
    <>
        <div className="mb-3 p-3" style={{ backgroundColor: '#fff', borderRadius: 8, whiteSpace: 'pre-wrap', fontSize: 13 }}>{resultado.explicacao}</div>
        <TabelaDiagnosticos diagnosticos={resultado.diagnosticos} />
    </>
);

// ── Comando único: plano por conta ─────────────────────────────────────────────────────────

const usd = (valor: number) => `US$ ${valor < 0.01 ? valor.toFixed(4) : valor.toFixed(2)}`;

const SITUACAO: Record<string, { texto: string; cor: string }> = {
    PLANO: { texto: 'Plano pronto', cor: '#009EF7' },
    SEM_REGRA_NOVA: { texto: 'Sem regra nova', cor: '#7E8299' },
    SEM_PENDENTES: { texto: 'Sem pendentes', cor: '#50CD89' },
    NAO_PROCESSADA: { texto: 'Não processada', cor: '#FFC700' },
    APLICADA: { texto: 'Aplicada', cor: '#50CD89' },
    NAO_APLICADA: { texto: 'Não aplicada', cor: '#7E8299' }
};

const PlanoDaConta = ({ plano }: { plano: IPlanoConta }) => {
    const porIndice = new Map((plano.simulacao?.porRegra ?? []).map(p => [p.indice, p]));

    return (
        <div className="p-3" style={{ backgroundColor: '#fff', borderRadius: 8, fontSize: 12 }}>
            {plano.analise && (
                <div className="mb-3">
                    <p className="mb-1">
                        <strong>Padrão de conciliação:</strong> {plano.analise.padraoConciliacao}{' '}
                        {plano.analise.qualidadeHistorico && (
                            <span className="badge" style={{ backgroundColor: CORES_QUALIDADE[plano.analise.qualidadeHistorico] ?? '#7E8299' }}>histórico {plano.analise.qualidadeHistorico}</span>
                        )}
                    </p>
                    {plano.analise.riscos.length > 0 && <ul className="mb-1">{plano.analise.riscos.map((r, i) => <li key={i}>{r}</li>)}</ul>}
                </div>
            )}

            {plano.regras.length > 0 && (
                <div className="table-responsive">
                    <table className="table table-sm table-row-bordered align-middle" style={{ fontSize: 12 }}>
                        <thead>
                            <tr className="fw-bold text-muted">
                                <th>#</th>
                                <th>Origem</th>
                                <th>Classe / parâmetros</th>
                                <th className="text-end">Conciliados</th>
                                <th className="text-end">Com diferença</th>
                                <th className="text-end">Valor</th>
                                <th>Por quê</th>
                            </tr>
                        </thead>
                        <tbody>
                            {plano.regras.map((regra, i) => {
                                const stats = porIndice.get(i);
                                return (
                                    <tr key={i} style={{ opacity: regra.descartada ? 0.55 : 1 }}>
                                        <td>{i + 1}</td>
                                        <td>{regra.existente ? 'Cadastrada' : `IA (rodada ${regra.rodada})`}</td>
                                        <td>
                                            <strong>{regra.classe}</strong>
                                            <div style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>{regra.parametros}</div>
                                        </td>
                                        <td className="text-end">{stats ? stats.conciliados.toLocaleString('pt-BR') : '—'}</td>
                                        <td className="text-end">{stats ? stats.divergentes.toLocaleString('pt-BR') : '—'}</td>
                                        <td className="text-end">{stats ? brl.format(stats.valorConciliado) : '—'}</td>
                                        <td>{regra.descartada ? <span className="text-danger">Descartada: {regra.motivoDescarte}</span> : regra.justificativa}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {plano.alternativasConsideradas.length > 0 && (
                <div className="mb-2">
                    <strong>Alternativas que a IA avaliou e não escolheu</strong>
                    <ul className="mb-0">{plano.alternativasConsideradas.map((a, i) => <li key={i}>{a}</li>)}</ul>
                </div>
            )}

            {plano.gruposDivergentes.length > 0 && (
                <div className="mb-2">
                    <strong>Exemplos de grupos que ficariam com diferença de valor</strong>
                    {plano.gruposDivergentes.map((g, i) => (
                        <div key={i} className="mt-1">
                            <span className="text-muted">Regra {g.indice + 1}: {g.motivo}</span>
                            <ul className="mb-0" style={{ fontFamily: 'monospace', fontSize: 11 }}>
                                {g.lancamentos.map((l, j) => <li key={j}>{l.tipo} {l.data} {brl.format(l.valor)} — {l.historico}</li>)}
                            </ul>
                        </div>
                    ))}
                </div>
            )}

            {plano.residuo && (
                <div className="mb-2">
                    <strong>O que continuaria pendente</strong>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{plano.residuo.explicacao}</div>
                    {plano.residuo.proximaAcao && <p className="mb-1 mt-1"><strong>Próxima ação:</strong> {plano.residuo.proximaAcao}</p>}
                    <details>
                        <summary className="text-muted">Diagnóstico de {plano.residuo.diagnosticos.length} lançamento(s) da amostra</summary>
                        <TabelaDiagnosticos diagnosticos={plano.residuo.diagnosticos} />
                    </details>
                </div>
            )}

            {plano.recusadasPelaValidacao.length > 0 && (
                <div className="mb-2">
                    <strong>Propostas recusadas pela validação:</strong>
                    <ul className="mb-0">{plano.recusadasPelaValidacao.map((r, i) => <li key={i} style={{ wordBreak: 'break-all' }}>{r}</li>)}</ul>
                </div>
            )}
            {plano.observacoes && <p style={{ whiteSpace: 'pre-wrap' }}><strong>Observações da IA:</strong> {plano.observacoes}</p>}
            {plano.avisoSimulacao && <p className="text-warning mb-1">{plano.avisoSimulacao}</p>}
            {plano.regrasGravadasIds.length > 0 && <p className="mb-1"><strong>Regras gravadas:</strong> {plano.regrasGravadasIds.join(', ')}</p>}
            <p className="text-muted mb-0">
                Custo da IA nesta conta: {usd(plano.custoRealUsd)} (provável {usd(plano.custoProvavelUsd)}, máximo {usd(plano.custoMaximoUsd)})
                {plano.rodadas > 0 ? ` · ${plano.rodadas} rodada(s)` : ''}
            </p>
        </div>
    );
};

const PlanoContas = ({ resultado, selecionadas, podeEscolher, onAlternar }: {
    resultado: IAnalisarConfigurarResultado;
    selecionadas: Set<number>;
    podeEscolher: boolean;
    onAlternar: (cartaoId: number) => void;
}) => {
    const [aberta, setAberta] = useState<number | null>(resultado.contas.length === 1 ? resultado.contas[0].cartaoId : null);
    const { estimativa } = resultado;

    return (
        <>
            <div className="mb-3 p-3" style={{ backgroundColor: '#fff', borderRadius: 8, fontSize: 13 }}>
                <strong>Custo da IA:</strong> provável {usd(estimativa.provavelUsd)} · máximo {usd(estimativa.maximoUsd)}
                <div className="text-muted" style={{ fontSize: 12 }}>
                    O gasto nunca passa do máximo.
                    {estimativa.semHistorico ? ' O provável ainda usa um padrão fixo: não há execuções suficientes para calibrar pelo consumo real.' : ' O provável vem do consumo real das últimas execuções.'}
                </div>
            </div>
            {resultado.consolidacao && (
                <div className="mb-3 p-3" style={{ backgroundColor: '#fff', borderRadius: 8, whiteSpace: 'pre-wrap', fontSize: 13 }}>
                    <strong>Parecer geral</strong>
                    <div className="mt-2">{resultado.consolidacao}</div>
                </div>
            )}
            <div className="table-responsive">
                <table className="table table-sm table-row-bordered align-middle" style={{ fontSize: 12 }}>
                    <thead>
                        <tr className="fw-bold text-muted">
                            {podeEscolher && <th>Aplicar</th>}
                            <th>Conta</th>
                            <th>Situação</th>
                            <th className="text-end">Lançamentos</th>
                            <th className="text-end">Conciliado</th>
                            <th className="text-end">Regras novas</th>
                            <th className="text-end">Custo provável / máximo</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {resultado.contas.map(plano => {
                            const situacao = SITUACAO[plano.situacao] ?? { texto: plano.situacao, cor: '#7E8299' };
                            const novas = plano.regras.filter(r => !r.existente && !r.descartada).length;
                            return (
                                <Fragment key={plano.cartaoId}>
                                    <tr>
                                        {podeEscolher && (
                                            <td>
                                                {plano.situacao === 'PLANO' && (
                                                    <input
                                                        type="checkbox"
                                                        aria-label={`Aplicar a conta ${plano.conta}`}
                                                        checked={selecionadas.has(plano.cartaoId)}
                                                        onChange={() => onAlternar(plano.cartaoId)}
                                                    />
                                                )}
                                            </td>
                                        )}
                                        <td><strong>{plano.conta}</strong><div className="text-muted">{plano.descricao}</div></td>
                                        <td><span className="badge" style={{ backgroundColor: situacao.cor }}>{situacao.texto}</span></td>
                                        <td className="text-end">{plano.metricas.totalLinhas.toLocaleString('pt-BR')}</td>
                                        <td className="text-end">
                                            {plano.simulacao
                                                ? <>{plano.simulacao.percentualAntes}% → <strong>{plano.simulacao.percentualDepois}%</strong></>
                                                : `${plano.metricas.percentualConciliado}%`}
                                        </td>
                                        <td className="text-end">{novas}</td>
                                        <td className="text-end">{usd(plano.custoProvavelUsd)} / {usd(plano.custoMaximoUsd)}</td>
                                        <td>
                                            {(plano.analise || plano.regras.length > 0) && (
                                                <button className="btn btn-sm btn-light" onClick={() => setAberta(aberta === plano.cartaoId ? null : plano.cartaoId)}>
                                                    {aberta === plano.cartaoId ? 'Fechar' : 'Detalhes'}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                    {aberta === plano.cartaoId && (
                                        <tr>
                                            <td colSpan={podeEscolher ? 8 : 7}><PlanoDaConta plano={plano} /></td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {resultado.aviso && <p className="text-muted mb-0" style={{ fontSize: 11 }}>{resultado.aviso}</p>}
        </>
    );
};

interface Props {
    comando: IAssistenteComando | null;
    onFechar: () => void;
    // cartaoIds: contas escolhidas no plano de várias contas (ausente = todas / comando antigo).
    onAplicar?: (comando: IAssistenteComando, cartaoIds?: number[]) => void;
    onDescartar?: (comando: IAssistenteComando) => void;
}

// DEVN3-609: relatório completo de um comando do assistente — o chat (340px) só mostra o resumo.
const AssistenteIaRelatorioModal = ({ comando, onFechar, onAplicar, onDescartar }: Props) => {
    const plano = useMemo(
        () => (comando?.tipo === 'ANALISAR_CONFIGURAR' ? lerResultado<IAnalisarConfigurarResultado>(comando) : null),
        [comando]);
    // Contas desmarcadas pelo usuário; por padrão todas as que têm plano são aplicadas.
    const [desmarcadas, setDesmarcadas] = useState<Set<number>>(new Set());

    if (!comando) return null;

    const aguardandoAplicacao = comando.status === 'AGUARDANDO_APLICACAO';
    const selecionadas = new Set((plano?.contas ?? []).filter(c => c.situacao === 'PLANO' && !desmarcadas.has(c.cartaoId)).map(c => c.cartaoId));
    const alternar = (cartaoId: number) => setDesmarcadas((old) => {
        const novo = new Set(old);
        if (novo.has(cartaoId)) novo.delete(cartaoId); else novo.add(cartaoId);
        return novo;
    });

    const titulo = comando.tipo === 'ANALISAR_CONFIGURAR'
        ? 'Análise e plano de regras de conciliação'
        : comando.tipo === 'ANALISE_CLIENTES_FORNECEDORES'
        ? 'Análise de contas de clientes e fornecedores'
        : comando.tipo === 'CONFIGURAR_REGRAS' ? 'Proposta de regras de conciliação' : 'Por que não conciliou';

    let corpo: JSX.Element = <p>{comando.resumo ?? comando.erro ?? 'Sem resultado.'}</p>;
    if (plano) {
        corpo = <PlanoContas resultado={plano} selecionadas={selecionadas} podeEscolher={aguardandoAplicacao} onAlternar={alternar} />;
    } else if (comando.tipo === 'ANALISE_CLIENTES_FORNECEDORES') {
        const r = lerResultado<IAnaliseContasResultado>(comando);
        if (r) corpo = <Analise resultado={r} />;
    } else if (comando.tipo === 'CONFIGURAR_REGRAS') {
        const r = lerResultado<IConfigurarRegrasResultado>(comando);
        if (r) corpo = <Regras resultado={r} />;
    } else {
        const r = lerResultado<IExplicarLancamentosResultado>(comando);
        if (r) corpo = <Explicacao resultado={r} />;
    }

    return (
        <Modal size="xl" centered={true} show={true} onHide={onFechar}>
            <div className="modal-content" style={{ backgroundColor: '#F4F5F7' }}>
                <div className="modal-header">
                    <h5 className="modal-title titulo-modal" style={{ textTransform: 'uppercase' }}>{titulo}</h5>
                    <div className="btn btn-icon btn-sm btn-active-light-primary ms-2">
                        <button onClick={onFechar} style={{ backgroundColor: 'none', border: 'none' }}>
                            <i className="fas fa-times" style={{ color: '#5E6397' }}></i>
                        </button>
                    </div>
                </div>
                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <p className="text-muted" style={{ fontSize: 12 }}>
                        {comando.descricao} · custo da IA: US$ {comando.custoRealUsd.toFixed(4)} ({plano ? 'máximo' : 'estimado até'} US$ {comando.custoEstimadoUsd.toFixed(4)})
                    </p>
                    {corpo}
                </div>
                {aguardandoAplicacao && (
                    <div className="modal-footer">
                        <button className="btn btn-sm btn-light" onClick={() => onDescartar?.(comando)}>Descartar proposta</button>
                        {plano ? (
                            <button className="btn btn-sm btn-primary" disabled={selecionadas.size === 0} onClick={() => onAplicar?.(comando, Array.from(selecionadas))}>
                                Aplicar {selecionadas.size} conta(s) e conciliar
                            </button>
                        ) : (
                            <button className="btn btn-sm btn-primary" onClick={() => onAplicar?.(comando)}>Aplicar regras e conciliar</button>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default AssistenteIaRelatorioModal;
